// Wat gaat er mee als een onderzoek de prullenbak in gaat?
//
// Meetmomenten horen bij precies één onderzoek en gaan altijd mee. Een
// vragenlijst kan door meerdere onderzoeken gedeeld worden ('Vragenlijst
// Gelijke Kansen' hangt aan twee), dus die wordt alleen aangeboden als geen
// ander levend onderzoek hem gebruikt. Geplande taken staan in de agenda van
// medewerkers; die verdwijnen niet stil maar op verzoek.
// AI-CONTEXT: See docs/decisions.md (ADR-008)

const levend = (doc) => doc && !doc.Deleted

/**
 * @typedef {import('../../components/common/DeleteModal').LinkedDeleteOption} LinkedDeleteOption
 */

/**
 * De vinkjes voor het verwijdervenster van een onderzoek.
 *
 * @param {object} params
 * @param {{ID: string, QuestionnaireID?: string}} params.research - Het onderzoek dat weggaat
 * @param {Array<{ID: string, QuestionnaireID?: string, Deleted?: boolean}>} params.researches - Alle onderzoeken van de organisatie
 * @param {Array<{ID: string, Title?: string, Deleted?: boolean}>} params.questionnaires - Alle vragenlijsten
 * @param {number} params.taskCount - Aantal levende geplande taken bij dit onderzoek
 * @returns {LinkedDeleteOption[]}
 */
const researchDeleteOptions = ({ research, researches, questionnaires, taskCount }) => {
    const questionnaire = (questionnaires || []).find((q) => levend(q) && q.ID === research?.QuestionnaireID)
    const gedeeld = (researches || []).some((r) => levend(r) && r.ID !== research?.ID && r.QuestionnaireID === research?.QuestionnaireID)
    const taken = taskCount || 0

    return [
        {
            key: 'tasks',
            label: `Verwijder ook de ${taken} geplande ${taken === 1 ? 'taak' : 'taken'} uit de agenda`,
            count: taken,
        },
        {
            key: 'questionnaire',
            label: `Verwijder ook de vragenlijst '${questionnaire?.Title || ''}' (geen ander onderzoek gebruikt hem)`,
            count: questionnaire && !gedeeld ? 1 : 0,
        },
    ]
}

/**
 * @typedef {object} DeleteTarget
 * @property {string} collection - Firestore-collectie
 * @property {string} docid - Document-id
 * @property {string} title - Wat de gebruiker in de prullenbak ziet
 */

/**
 * Wat er, in volgorde, zacht verwijderd wordt naast het onderzoek zelf.
 *
 * @param {object} params
 * @param {Array<{docid: string, Title?: string, Deleted?: boolean}>} params.moments - Meetmomenten van het onderzoek
 * @param {Array<{docid: string, Title?: string, Deleted?: boolean}>} params.tasks - Geplande taken van het onderzoek
 * @param {{docid: string, Title?: string}|null|undefined} params.questionnaire - De vragenlijst, als die exclusief is
 * @param {string[]} params.selectedKeys - Aangevinkte sleutels uit het verwijdervenster
 * @returns {DeleteTarget[]}
 */
const researchDeleteTargets = ({ moments, tasks, questionnaire, selectedKeys }) => {
    const gekozen = new Set(selectedKeys || [])
    const naar = (collection) => (doc) => ({ collection, docid: doc.docid, title: doc.Title || '' })

    const doelen = (moments || []).filter(levend).map(naar('MeasureMoments'))
    if (gekozen.has('tasks')) doelen.push(...(tasks || []).filter(levend).map(naar('Tasks')))
    if (gekozen.has('questionnaire') && questionnaire?.docid) doelen.push(naar('Questionnaires')(questionnaire))
    return doelen
}

export { researchDeleteOptions, researchDeleteTargets }
